import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { getImageUrl } from '../utils/cdn';
import { getApiUrl } from '../utils/apiUrl';
import './Shop.css';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [filters, searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/products/categories/all`);
      // Ensure categories is always an array
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Set empty array on error
      setCategories([]);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const page = searchParams.get('page') || 1;
      const params = new URLSearchParams({
        page,
        limit: 12
      });

      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(
        `${getApiUrl()}/products?${params}`
      );
      // Ensure products is always an array
      setProducts(response.data?.products || []);
      setPagination(response.data?.pagination || { page: 1, pages: 1 });
    } catch (error) {
      console.error('Failed to fetch products:', error);
      // Set empty array on error
      setProducts([]);
      setPagination({ page: 1, pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setSearchParams({ ...newFilters, page: 1 });
  };

  const handlePageChange = (page) => {
    setSearchParams({ ...filters, page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Shop - NieHairLabel</title>
        <meta name="description" content="Browse our collection of premium wigs" />
      </Helmet>

      <div className="shop-page">
        <div className="container">
          <h1>Shop</h1>

          <div className="shop-content">
            <aside className="filters">
              <h3>Filters</h3>
              
              <div className="filter-group">
                <label>Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search products..."
                />
              </div>

              <div className="filter-group">
                <label>Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories && categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {(filters.category || filters.search) && (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setFilters({ category: '', search: '' });
                    setSearchParams({});
                  }}
                >
                  Clear Filters
                </button>
              )}
            </aside>

            <main className="products-section">
              {loading ? (
                <div className="loading">Loading products...</div>
              ) : products && products.length > 0 ? (
                <>
                  <div className="products-grid">
                    {products.map(product => (
                      <div key={product.id} className="product-card">
                        <Link to={`/shop/${product.slug}`}>
                          <div className="product-image">
                            {product.image_url ? (
                              <img
                                src={getImageUrl(product.image_url)}
                                alt={product.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="placeholder-image">No Image</div>
                            )}
                            {product.stock_status === 'OUT OF STOCK' && (
                              <span className="sold-out-badge">Out of Stock</span>
                            )}
                          </div>
                          <div className="product-info">
                            <h3>{product.name}</h3>
                            <div className="product-price">
                              <span className="price">${product.price}</span>
                              {product.compare_price && (
                                <span className="compare-price">${product.compare_price}</span>
                              )}
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>

                  {pagination.pages > 1 && (
                    <div className="pagination">
                      <button
                        className="btn btn-secondary"
                        disabled={pagination.page === 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                      >
                        Previous
                      </button>
                      <span>
                        Page {pagination.page} of {pagination.pages}
                      </span>
                      <button
                        className="btn btn-secondary"
                        disabled={pagination.page === pagination.pages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-products">
                  <p>No products found.</p>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default Shop;

